// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { Dropdown } from "react-bootstrap";

import { isSet } from "shared/utils/utils";
import { IState } from "states/states";
import { IAction } from "actions";

interface ISearchableDropdownExtProps<ItemType> {
  caption?: string;
  valueList: ItemType[];
  mapperFunction: (value: ItemType | undefined) => void;
  labelFunction: (value: ItemType) => string;
  curItem: string | JSX.Element | undefined;
  nextElementId?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

interface ISearchableDropdownProps {}

const mapStateToProps = (state: IState) => {
  const props: ISearchableDropdownProps = {};
  return props;
};

interface ISearchableDropdownState<ItemType> {
  isOpen: boolean;
  searchText: string;
  selectedItem?: ItemType;
}

interface ISearchableDropdownDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISearchableDropdownDispatch = {};
  return dispatchProps;
};

class SearchableDropdownComponent<ItemType> extends React.Component<
  ISearchableDropdownProps & ISearchableDropdownExtProps<ItemType> & ISearchableDropdownDispatch,
  ISearchableDropdownState<ItemType>
> {
  public state: ISearchableDropdownState<ItemType> = {
    isOpen: false,
    searchText: "",
  };

  private isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  private inputRef = React.createRef<HTMLInputElement>();

  private onKeyEvent(event: React.KeyboardEvent) {
    // handle special events
    if (event.key === "Escape") {
      if (this.state.isOpen && this.state.searchText.length > 0) {
        // clear input
        this.setState({ searchText: "", selectedItem: undefined });
      } else if (this.state.isOpen) {
        // close dropdown and save selection
        this.setState({ isOpen: false });
        this.props.mapperFunction(this.state.selectedItem);
      }
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    if (event.key === "Tab") {
      if (this.state.isOpen) {
        // focus button, if given
        if (this.props.nextElementId) {
          const nextElement = document.getElementById(this.props.nextElementId);
          nextElement?.removeAttribute("disabled"); // remove disabled attribute to allow focus
          nextElement?.focus();
          event.stopPropagation();
          event.preventDefault();
        }
        // close dropdown and report selection to parent component
        this.setState({ isOpen: false, searchText: "" });
        this.props.mapperFunction(this.state.selectedItem);
      }
      return;
    }

    // valid character input?
    if (
      ["Backspace", "Delete"].includes(event.key) ||
      (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey)
    ) {
      let newName = this.state.searchText;
      if (event.key === "Backspace" || event.key === "Delete") {
        newName = newName.substring(0, newName.length - 1);
      } else if (event.key.length === 1) {
        newName += event.key.toLowerCase();
      }

      // open dropdown if not already open
      if (!this.state.isOpen) {
        this.setState({ isOpen: true });
      }

      this.setState({ searchText: newName });
      const newValues = this.getFilteredValueList(newName);
      this.setState({ selectedItem: newValues.length ? newValues[0] : undefined });

      // keep focus on the toggle while typing so that focused dropdown items
      // cannot receive a browser-native space→click
      setTimeout(() => {
        document.getElementById("searchable-dropdown-toggle")?.focus();
      }, 0);

      event.stopPropagation();
      event.preventDefault();
    }
  }

  private getFilteredValueList(filter: string): ItemType[] {
    return this.props.valueList.filter(
      (curItem) =>
        filter.length === 0 || this.props.labelFunction(curItem).toLowerCase().includes(filter),
    );
  }

  private getDropdownItems() {
    const valueList = this.getFilteredValueList(this.state.searchText);
    const dropdownItems = valueList.map((curValue) => {
      return (
        <Dropdown.Item
          key={`${curValue}`}
          id={`searchable_${curValue}`}
          onClick={() => this.props.mapperFunction(curValue)}
          onFocus={() => this.setState({ selectedItem: curValue })}
        >
          {this.props.labelFunction(curValue)}
        </Dropdown.Item>
      );
    });
    return dropdownItems;
  }

  public render() {
    const items = this.getDropdownItems();
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        {this.isSafari && this.state.isOpen && (
          <input
            ref={this.inputRef}
            type="text"
            value={this.state.searchText}
            onChange={(e) => {
              const newText = e.target.value;
              this.setState({ searchText: newText });
              const newValues = this.getFilteredValueList(newText);
              this.setState({ selectedItem: newValues.length ? newValues[0] : undefined });
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                this.setState({ isOpen: false, searchText: "", selectedItem: undefined });
                this.props.mapperFunction(this.state.selectedItem);
              } else if (e.key === "Enter" && this.state.selectedItem !== undefined) {
                this.setState({ isOpen: false, searchText: "" });
                this.props.mapperFunction(this.state.selectedItem);
              } else if (e.key === "Tab") {
                if (this.props.nextElementId) {
                  const nextElement = document.getElementById(this.props.nextElementId);
                  nextElement?.removeAttribute("disabled");
                  nextElement?.focus();
                  e.stopPropagation();
                  e.preventDefault();
                }
                this.setState({ isOpen: false, searchText: "" });
                this.props.mapperFunction(this.state.selectedItem);
              }
            }}
            autoFocus
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0,
              opacity: 0,
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
        )}
        <Dropdown
          className={this.props.className}
          show={this.state.isOpen}
          onToggle={(isOpen) => {
            if (!isOpen) {
              // update selection on close event
              this.props.mapperFunction(this.state.selectedItem);
              this.setState({ searchText: "", selectedItem: undefined });
            }
            this.setState({ isOpen }, () => {
              // focus the input field in Safari when dropdown opens
              if (this.isSafari && isOpen) {
                setTimeout(() => this.inputRef.current?.focus(), 0);
              }
            });
          }}
        >
          <Dropdown.Toggle
            variant="secondary"
            id="searchable-dropdown-toggle"
            style={{ marginTop: -2, ...this.props.style }}
            className={this.props.disabled ? "disabled" : undefined}
            onKeyDown={(event) => !this.isSafari && this.onKeyEvent(event)}
            onKeyPress={(event) => !this.isSafari && this.onKeyEvent(event)}
            onClick={() => {
              if (!this.state.isOpen) {
                this.setState({ isOpen: true });
              }
            }}
            disabled={this.props.disabled}
          >
            {this.state.isOpen ?
              this.state.searchText.length > 0 ?
                this.state.searchText
              : this.props.caption
            : (this.props.curItem ?? this.props.caption)}
          </Dropdown.Toggle>
          <Dropdown.Menu onKeyDown={(event) => !this.isSafari && this.onKeyEvent(event)}>
            {isSet(this.props.caption) && <Dropdown.Header>{this.props.caption}</Dropdown.Header>}
            {items}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }
}

export function SearchableDropdown<ItemType>(
  props: ISearchableDropdownExtProps<ItemType> & ISearchableDropdownProps,
) {
  const ConnectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps,
  )(SearchableDropdownComponent<ItemType>);

  return <ConnectedComponent {...props} />;
}
export default SearchableDropdown;
